import { useState } from 'react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Search } from 'lucide-react';
import { WebsiteFilter } from '@/components/website/WebsiteFilter';
import { NoteCard } from '@/components/website/NoteCard';
import { NoteInput } from '@/components/website/NoteInput';
import { useWebsiteNotes } from '@/hooks/useWebsiteNotes';
import { useAuth } from '@/hooks/useAuth';
import type { WebsiteName } from '@/types/website';

export default function CRMWebsiteNotes() {
  const { user } = useAuth();
  const [websiteFilter, setWebsiteFilter] = useState<WebsiteName | 'all'>('all');
  const [search, setSearch] = useState('');
  const [labelFilter, setLabelFilter] = useState<string | null>(null);

  const filter = websiteFilter === 'all' ? undefined : websiteFilter;
  const { notes, allLabels, createNote, deleteNote } = useWebsiteNotes(filter);

  const filtered = notes.filter(n => {
    const matchesSearch = n.note.toLowerCase().includes(search.toLowerCase());
    const matchesLabel = !labelFilter || n.labels?.includes(labelFilter);
    return matchesSearch && matchesLabel;
  });

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Website Notities</h1>
        <WebsiteFilter value={websiteFilter} onChange={setWebsiteFilter} />
      </div>

      <NoteInput
        onSubmit={(data) => createNote.mutate(data)}
        defaultWebsite={websiteFilter === 'all' ? undefined : websiteFilter}
        isLoading={createNote.isPending}
        suggestedLabels={allLabels}
      />

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Zoeken..."
            className="pl-9"
          />
        </div>
        {allLabels.length > 0 && (
          <div className="flex gap-1 flex-wrap">
            <Badge
              variant={labelFilter === null ? 'default' : 'outline'}
              className="cursor-pointer"
              onClick={() => setLabelFilter(null)}
            >
              Alle
            </Badge>
            {allLabels.slice(0, 5).map(label => (
              <Badge
                key={label}
                variant={labelFilter === label ? 'default' : 'outline'}
                className="cursor-pointer"
                onClick={() => setLabelFilter(label === labelFilter ? null : label)}
              >
                {label}
              </Badge>
            ))}
          </div>
        )}
      </div>

      <div className="space-y-3">
        {filtered.map(note => (
          <NoteCard
            key={note.id}
            note={note}
            canEdit={note.created_by === user?.id}
            onDelete={() => deleteNote.mutate(note.id)}
          />
        ))}
        {filtered.length === 0 && (
          <p className="text-muted-foreground text-center py-8">Geen notities gevonden</p>
        )}
      </div>
    </div>
  );
}